import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { ViewLarge, Viewport } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { GalleryViewModel } from 'models';

import { UitkCardMedia } from 'uitk-react-cards';
import { UitkFigure, UitkImage } from 'uitk-react-images';

import { GalleryCarousel } from 'components/common/media/gallery-carousel';
import { GallerySwipe } from 'components/common/media/gallery-swipe';
import { GalleryTrigger } from 'components/infosite/property-details/gallery-trigger';
import { QueuedImage } from 'components/common/media/queued-image';

export type LocalizedInfositeGalleryProps = LocalizedComponentProps & InfositeGalleryProps;

export interface InfositeGalleryProps {
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  gallery?: GalleryViewModel;
  hotelId: number;
  hotelName?: string;
  id: number;
  routeStore?: RouteStore;
  favoriteButton: JSX.Element;
}

export interface InfositeGalleryState {
  currentGalleryIndex: number;
}

@withLocalization
@inject('analytics', 'currentHotel', 'routeStore')
@observer
export class InfositeGallery extends React.Component<LocalizedInfositeGalleryProps, InfositeGalleryState> {
  constructor(props) {
    super(props);

    this.openPhotoGalleryDialog = this.openPhotoGalleryDialog.bind(this);
    this.updateIndex = this.updateIndex.bind(this);
  }

  private openPhotoGalleryDialog(): void {
    this.props.routeStore.updateSearch({ pwaDialog: `photoGallery-${this.props.id}` });
    this.props.analytics.clickTracker('.LaunchMainPhotoGallery', 'Launch Photo Gallery From Image Click', null, () => null)();
  }

  private updateIndex(index: number): void {
    const { currentHotel } = this.props;

    if (this.canUpdateIndex()) {
      currentHotel.setCurrentGalleryIndex(index);
    }
  }

  private canUpdateIndex() {
    const { currentHotel } = this.props;
    const { selectedGalleryCategory } = currentHotel;

    return (
      currentHotel &&
      currentHotel.setCurrentGalleryIndex &&
      typeof currentHotel.setCurrentGalleryIndex === 'function' &&
      (!selectedGalleryCategory || selectedGalleryCategory.id === CurrentHotelStore.DEFAULTS.selectedGalleryCategoryId)
    );
  }

  public render() {
    const { analytics, gallery, hotelId, hotelName, l10n, favoriteButton } = this.props;
    const currentGalleryIndex = (this.props.currentHotel && this.props.currentHotel.photoGalleryIndex) || 0;

    return (
      <React.Fragment>
        {(!gallery || !gallery.images || !gallery.images.length) && (
          <UitkCardMedia>
            <UitkFigure>
              <UitkImage alt="" placeholderImage={true} />
            </UitkFigure>
          </UitkCardMedia>
        )}
        {gallery &&
          gallery.images &&
          gallery.images.length > 0 && (
            <UitkCardMedia>
              <AbacusExperiment name="PWA_UDS_Gallery_Carousel_Enable">
                <AbacusControl>
                  <React.Fragment>
                    <meta itemProp="image" content={gallery.images[0].src} />
                    <div className="is-visually-hidden">
                      <LocalizedText message="hotels.infosite.gallery.a11yLabel" />
                    </div>
                    <ErrorBoundary
                      label="hotel.information.details.photoGallery"
                      fallback={
                        <div className="photo-gallery">
                          <QueuedImage
                            className="photo-gallery__image image-loader"
                            src={gallery.images && gallery.images[0] && gallery.images[0].src}
                            useBackgroundImage={true}
                          />
                        </div>
                      }
                    >
                      <AbacusExperiment name="pwa_gallery_stop_preloading">
                        <AbacusControl>
                          <GallerySwipe
                            galleryName="Main"
                            images={gallery.images}
                            currentIndex={currentGalleryIndex}
                            onIndexChange={this.updateIndex}
                            onImageClick={this.openPhotoGalleryDialog}
                          />
                        </AbacusControl>
                        <AbacusVariant bucket={1}>
                          <GallerySwipe
                            galleryName="Main"
                            images={gallery.images}
                            currentIndex={currentGalleryIndex}
                            onIndexChange={this.updateIndex}
                            onImageClick={this.openPhotoGalleryDialog}
                            enablePreload={false}
                          />
                        </AbacusVariant>
                      </AbacusExperiment>
                      <Viewport>
                        <ViewLarge>{favoriteButton}</ViewLarge>
                      </Viewport>
                    </ErrorBoundary>
                    <ErrorBoundary label="hotel.information.details.galleryTrigger">
                      <GalleryTrigger
                        className="room__info-trigger"
                        currentIndex={currentGalleryIndex}
                        id={hotelId}
                        gallery={gallery}
                        onIndexChange={this.updateIndex}
                        trackingRfrr=".PhotoGalleryTrigger"
                        galleryTitle={hotelName}
                        showThumbnail={true}
                        infositeGallery={true}
                      />
                    </ErrorBoundary>
                  </React.Fragment>
                </AbacusControl>
                <AbacusVariant bucket={1}>
                  <React.Fragment>
                    <ErrorBoundary
                      label="hotel.information.details.photoGallery"
                      fallback={
                        <div className="photo-gallery">
                          <QueuedImage
                            className="photo-gallery__image image-loader"
                            src={gallery.images && gallery.images[0] && gallery.images[0].src}
                            useBackgroundImage={true}
                          />
                        </div>
                      }
                    >
                      <meta itemProp="image" content={gallery.images[0].src} />
                      <GalleryCarousel
                        allyHeadingLevel={2}
                        allyHeadingText={l10n.formatText('hotels.infosite.gallery.a11yLabel')}
                        analytics={analytics}
                        carouselName="Main"
                        currentIndex={currentGalleryIndex}
                        images={gallery.images}
                        onImageClick={this.openPhotoGalleryDialog}
                        onIndexChange={this.updateIndex}
                        onMediaButtonClick={this.openPhotoGalleryDialog}
                        showGalleryButton={false}
                      />
                      <Viewport>
                        <ViewLarge>{favoriteButton}</ViewLarge>
                      </Viewport>
                    </ErrorBoundary>
                    <ErrorBoundary label="hotel.information.details.galleryTrigger">
                      <GalleryTrigger
                        className="uitk-gallery-button"
                        currentIndex={currentGalleryIndex}
                        displayImageCount={true}
                        id={hotelId}
                        gallery={gallery}
                        onIndexChange={this.updateIndex}
                        trackingRfrr=".PhotoGalleryTrigger"
                        galleryTitle={hotelName}
                        showThumbnail={true}
                        infositeGallery={true}
                      />
                    </ErrorBoundary>
                  </React.Fragment>
                </AbacusVariant>
              </AbacusExperiment>
            </UitkCardMedia>
          )}
      </React.Fragment>
    );
  }
}
